import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { todayDateKey } from '@features/quote/utils/dateKey';
import {
    AUTO_MATCH_OPTION,
    NO_SERVICE_OPTION,
    serviceOptionIdFrom,
    smriqLastCompletedServiceSchema,
    type SmriqLastCompletedServiceFormValues,
} from '../schemas/smriqLastCompletedService.schema';
import type {
    SmriqLastCompletedServiceOption,
    SmriqLastCompletedServiceSelection,
} from '../types/smriq.types';
import { isAutoMatch } from '../utils/isAutoMatch';
import type { LastCompletedServiceFieldErrors } from '../utils/lastCompletedServiceFieldErrors';
import {
    buildSelectOptions,
    earlierDateKey,
    toFormValues,
    type HeldServiceOption,
    toSelection,
} from '../utils/lastCompletedServiceForm';

const SERVER_ERROR_FIELDS = ['service_option_id', 'completed_date', 'completed_mileage'] as const;

const heldOption = (selection: SmriqLastCompletedServiceSelection | null): HeldServiceOption | null =>
    isAutoMatch(selection) ? null : selection;

interface UseLastCompletedServiceFormArgs {
    options: readonly SmriqLastCompletedServiceOption[];
    selection: SmriqLastCompletedServiceSelection | null;
    resetKey: number;
    planStartDate?: string;
    vehicleLastServiceDate?: string;
    vehicleLastServiceMileage?: number;
    serverErrors: LastCompletedServiceFieldErrors;
    onChange: (selection: SmriqLastCompletedServiceSelection | null, valid: boolean) => void;
}

export function useLastCompletedServiceForm({
    options,
    selection,
    resetKey,
    planStartDate,
    vehicleLastServiceDate,
    vehicleLastServiceMileage,
    serverErrors,
    onChange,
}: UseLastCompletedServiceFormArgs) {
    const maxDate = earlierDateKey(todayDateKey(), planStartDate);
    // The chosen service outlives an incomplete edit of its details; only a reset or another
    // choice drops it, so a held option missing from the vendor list stays selectable.
    const [held, setHeld] = useState<{
        seen: SmriqLastCompletedServiceSelection | null;
        option: HeldServiceOption | null;
    }>({ seen: selection, option: heldOption(selection) });
    if (selection !== held.seen) {
        setHeld({ seen: selection, option: selection ? heldOption(selection) : held.option });
    }
    const draftOption = held.option;

    const optionIdsKey = JSON.stringify([
        ...options.map((option) => option.id),
        draftOption?.service_option_id ?? '',
    ]);
    const schema = useMemo(
        () => smriqLastCompletedServiceSchema(JSON.parse(optionIdsKey) as string[], maxDate),
        [optionIdsKey, maxDate],
    );
    const resolver = useMemo(() => zodResolver(schema), [schema]);

    const selectionRef = useRef(selection);
    selectionRef.current = selection;
    const draftOptionRef = useRef(draftOption);
    draftOptionRef.current = draftOption;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const capInputsRef = useRef({ options, maxDate });
    capInputsRef.current = { options, maxDate };

    const { control, setValue, getValues, setError, clearErrors, reset, getFieldState, trigger } =
        useForm<SmriqLastCompletedServiceFormValues>({
            mode: 'onChange',
            resolver,
            defaultValues: toFormValues(selection),
        });

    const values = useWatch({ control }) as SmriqLastCompletedServiceFormValues;
    const reportedKeyRef = useRef(JSON.stringify(toFormValues(selection)));
    const reportedValidRef = useRef(true);

    const capRef = useRef({ maxDate, resetKey });
    useEffect(() => {
        const previous = capRef.current;
        capRef.current = { maxDate, resetKey };
        // A reset in the same commit validates the restored selection against this cap itself.
        if (previous.maxDate === maxDate || previous.resetKey !== resetKey) return;
        const current = getValues();
        if (current.completed_date) void trigger('completed_date');
        const parsed = schema.safeParse(current);
        if (parsed.success === reportedValidRef.current) return;
        reportedValidRef.current = parsed.success;
        const next = parsed.success ? toSelection(current, options, draftOptionRef.current) : null;
        onChangeRef.current(next, parsed.success);
    }, [maxDate, resetKey, schema, options, getValues, trigger]);

    useEffect(() => {
        const key = JSON.stringify(values);
        if (key === reportedKeyRef.current) return;
        reportedKeyRef.current = key;
        const parsed = schema.safeParse(values);
        reportedValidRef.current = parsed.success;
        const next = parsed.success
            ? toSelection(values, options, draftOptionRef.current)
            : null;
        onChangeRef.current(next, parsed.success);
    }, [values, schema, options]);

    const isFirstResetRef = useRef(true);
    useEffect(() => {
        if (isFirstResetRef.current) {
            isFirstResetRef.current = false;
            return;
        }
        const restored = selectionRef.current;
        const next = toFormValues(restored);
        reportedKeyRef.current = JSON.stringify(next);
        setHeld({ seen: restored, option: heldOption(restored) });
        reset(next);
        const { options: listed, maxDate: cap } = capInputsRef.current;
        const optionIds = [
            ...listed.map((option) => option.id),
            heldOption(restored)?.service_option_id ?? '',
        ];
        const valid = smriqLastCompletedServiceSchema(optionIds, cap).safeParse(next).success;
        reportedValidRef.current = valid;
        if (valid) return;
        void trigger('completed_date');
        onChangeRef.current(null, false);
    }, [resetKey, reset, trigger]);

    const serverErrorsKey = JSON.stringify(serverErrors);
    useEffect(() => {
        const parsed = JSON.parse(serverErrorsKey) as LastCompletedServiceFieldErrors;
        SERVER_ERROR_FIELDS.forEach((field) => {
            const message = parsed[field];
            if (message) setError(field, { type: 'server', message });
            else if (getFieldState(field).error?.type === 'server') clearErrors(field);
        });
    }, [serverErrorsKey, setError, clearErrors, getFieldState]);

    const chooseOption = useCallback(
        (next: string) => {
            const validate = { shouldValidate: true };
            const nextId = serviceOptionIdFrom(next);
            if (nextId !== draftOptionRef.current?.service_option_id) {
                const chosen = options.find((option) => option.id === nextId);
                setHeld((previous) => ({
                    ...previous,
                    option: chosen ? { service_option_id: chosen.id, label: chosen.label } : null,
                }));
            }
            setValue('service_option_id', next, validate);
            if (next === NO_SERVICE_OPTION) {
                setValue('completed_date', '');
                setValue('completed_mileage', '');
                clearErrors();
                return;
            }
            if (!getValues('completed_date') && vehicleLastServiceDate) {
                setValue('completed_date', vehicleLastServiceDate, validate);
            }
            if (!getValues('completed_mileage') && vehicleLastServiceMileage != null) {
                setValue('completed_mileage', String(vehicleLastServiceMileage), validate);
            }
        },
        [
            options,
            setValue,
            getValues,
            clearErrors,
            vehicleLastServiceDate,
            vehicleLastServiceMileage,
        ],
    );

    return {
        control,
        maxDate,
        chooseOption,
        showDetails: values.service_option_id !== NO_SERVICE_OPTION,
        autoMatchChosen: values.service_option_id === AUTO_MATCH_OPTION,
        selectOptions: buildSelectOptions(options, draftOption),
    };
}
